import { collection, query, where, orderBy, onSnapshot, addDoc, Timestamp, updateDoc, deleteDoc, doc, getDocs, DocumentData, increment, runTransaction } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { handleFirebaseError } from '../error-handling';
import { updateGroupLastPost } from './groups';
import { toggleLike } from '@/lib/groups/groupPosts';

export interface Post {
  id: string;
  title: string;
  content: string;
  userId: string;
  userName: string;
  category: string;
  likes: number;
  comments: number;
  createdAt: Date;
  updatedAt: Date;
}

// Cache configuration
const POSTS_CACHE_KEY = 'groupPosts';
const CACHE_EXPIRY = 1000 * 60 * 5; // 5 minutes

async function getCachedPosts(groupId: string): Promise<Post[]> {
  try {
    const cache = localStorage.getItem(`${POSTS_CACHE_KEY}_${groupId}`);
    if (!cache) return [];
    
    const { posts, timestamp } = JSON.parse(cache);
    const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
    
    return isExpired ? [] : posts;
  } catch {
    return [];
  }
}

async function cachePosts(groupId: string, posts: Post[]) {
  try {
    localStorage.setItem(`${POSTS_CACHE_KEY}_${groupId}`, JSON.stringify({
      posts,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error caching posts:', error);
  }
}

function convertToPost(doc: DocumentData): Post {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.title,
    content: data.content,
    userId: data.userId,
    userName: data.userName,
    category: data.category,
    likes: data.likes,
    comments: data.comments,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate()
  };
}

export async function getGroupPosts(groupId: string): Promise<Post[]> {
  try {
    // Use a simple query without compound indexes
    const postsQuery = query(
      collection(db, 'groupPosts'),
      where('groupId', '==', groupId)
    );
    
    const snapshot = await getDocs(postsQuery);
    const posts = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt?.toDate() || doc.data().createdAt.toDate()
      }))
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) as Post[];

    await cachePosts(groupId, posts);
    return posts;
  } catch (error) {
    handleFirebaseError(error);
    return getCachedPosts(groupId);
  }
}

export function subscribeToGroupPosts(
  groupId: string,
  onUpdate: (posts: Post[]) => void,
  onError?: (error: Error) => void
): () => void {
  // Use a simple query without compound indexes
  const postsQuery = query(
    collection(db, 'groupPosts'),
    where('groupId', '==', groupId)
  );

  return onSnapshot(
    postsQuery,
    {
      next: async (snapshot) => {
        const posts = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate(),
            updatedAt: doc.data().updatedAt?.toDate() || doc.data().createdAt.toDate()
          }))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) as Post[];
        
        await cachePosts(groupId, posts);
        onUpdate(posts);
      },
      error: async (error) => {
        console.error('Posts subscription error:', error);
        handleFirebaseError(error);
        
        const cachedPosts = await getCachedPosts(groupId);
        if (cachedPosts.length > 0) {
          onUpdate(cachedPosts);
        }
        
        if (onError) onError(error);
      }
    }
  );
}

export async function getPosts(): Promise<Post[]> {
  try {
    const postsQuery = query(collection(db, 'posts'));
    const snapshot = await getDocs(postsQuery);
    
    const posts = snapshot.docs.map(convertToPost);
    return posts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    handleFirebaseError(error);
  }
}

export async function getPostsByCategory(category: string): Promise<Post[]> {
  try {
    const postsQuery = query(
      collection(db, 'posts'),
      where('category', '==', category)
    );
    
    const snapshot = await getDocs(postsQuery);
    const posts = snapshot.docs.map(convertToPost);
    return posts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    handleFirebaseError(error);
  }
}

export async function createPost(post: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  try {
    const docRef = await addDoc(collection(db, 'posts'), {
      ...post,
      likes: 0,
      comments: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    handleFirebaseError(error);
  }
}

export async function updatePost(id: string, data: Partial<Post>): Promise<void> {
  try {
    const postRef = doc(db, 'groupPosts', id);
    await updateDoc(postRef, {
      ...data,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    handleFirebaseError(error);
  }
}

export async function deletePost(id: string): Promise<void> {
  try {
    const postRef = doc(db, 'groupPosts', id);
    await deleteDoc(postRef);
  } catch (error) {
    handleFirebaseError(error);
  }
}

/**
 * Like or unlike a group post as `userId` (namat#36 P2). Likes are per member
 * (likedBy), so a member cannot unlike someone else's like and the count
 * never goes below 0. Resolves to whether the member now likes the post.
 */
export async function togglePostLike(postId: string, userId: string): Promise<boolean> {
  const postRef = doc(db, 'groupPosts', postId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(postRef);
    if (!snap.exists()) throw new Error('Post not found');
    const data = snap.data();
    const next = toggleLike({ likes: data.likes, likedBy: data.likedBy }, userId);
    tx.update(postRef, { likes: next.likes, likedBy: next.likedBy, updatedAt: Timestamp.now() });
    return next.liked;
  });
}

/** @deprecated Count-only; use togglePostLike. */
export async function likePost(postId: string): Promise<void> {
  try {
    const postRef = doc(db, 'groupPosts', postId);
    await updateDoc(postRef, {
      likes: increment(1),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    handleFirebaseError(error);
  }
}

/** @deprecated Count-only and can go negative; use togglePostLike. */
export async function unlikePost(postId: string): Promise<void> {
  try {
    const postRef = doc(db, 'groupPosts', postId);
    await updateDoc(postRef, {
      likes: increment(-1),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    handleFirebaseError(error);
  }
}